import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Users, FileText, Sparkles, RefreshCw, Trash2, Mail, ArrowLeft, Search, LifeBuoy, Download, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { MasterUtilisateurs, MasterAbonnement, MasterUsageIA, MasterJournaux } from "@/components/MasterOutils";

const STATUT = { essai: ["Essai", "bg-amber-100 text-amber-900"], actif: ["Client actif", "bg-green-100 text-green-800"], offert: ["Offert", "bg-sky-100 text-sky-900"], suspendu: ["Suspendu", "bg-red-100 text-red-800"] };
const PHASE = { essai_expire: ["Essai expiré · lecture seule", "bg-orange-100 text-orange-900"], bloque: ["Bloqué", "bg-red-100 text-red-800"] };
const StatutBadge = ({ a }) => {
  const ph = a.acces?.phase;
  const [l, c] = PHASE[ph] || STATUT[a.statut] || ["?", ""];
  return <Badge className={`border-0 ${c}`}>{l}</Badge>;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-CH") : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" }) : "jamais");

export default function Master() {
  const [stats, setStats] = useState(null);
  const [agences, setAgences] = useState([]);
  const [q, setQ] = useState("");
  const [filtre, setFiltre] = useState("tous");
  const [sel, setSel] = useState(null);

  const load = async () => {
    const [s, a] = await Promise.all([api.get("/master/stats"), api.get("/master/agences")]);
    setStats(s.data); setAgences(a.data);
  };
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/master/agences.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `madlo-gerances-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const rows = agences.filter((a) => (filtre === "tous" || a.statut === filtre) &&
    (!q || `${a.nom} ${a.admin_email || ""} ${a.admin_nom || ""}`.toLowerCase().includes(q.toLowerCase())));

  if (sel) return <AgenceDetail id={sel} onBack={() => { setSel(null); load(); }} />;

  return (
    <div data-testid="master-page">
      <h1 className="text-3xl font-bold text-dd-blue">Back-office MADLO</h1>
      <p className="text-sm text-dd-warm mt-1 mb-6">Gérances inscrites sur la plateforme, suivi commercial et usage.</p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi icon={Building2} label="Gérances" value={stats.agences} sub={`${stats.par_statut.actif} actives · ${stats.par_statut.offert} offertes · ${stats.par_statut.essai} en essai · ${stats.par_statut.essai_expire} expirées · ${stats.par_statut.suspendu} suspendues`} testid="master-kpi-agences" />
          <Kpi icon={Users} label="Utilisateurs" value={stats.utilisateurs} testid="master-kpi-users" />
          <Kpi icon={FileText} label="Constats" value={stats.constats} testid="master-kpi-constats" />
          <Kpi icon={Sparkles} label="Imports Claude" value={stats.imports} sub={`≈ ${stats.cout_imports_usd.toFixed(2)} USD${stats.imports_sans_usage ? ` (${stats.imports_sans_usage} sans mesure)` : ""}`} testid="master-kpi-imports" />
        </div>
      )}

      {stats?.par_mois?.length > 0 && (
        <div className="bg-white rounded-lg border border-dd-light mb-6 overflow-x-auto" data-testid="master-monthly">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-dd-warm border-b border-dd-light">
              <th className="px-5 py-2 font-medium">Mois</th><th className="px-3 py-2 text-right font-medium">Inscriptions</th><th className="px-3 py-2 text-right font-medium">Constats créés</th><th className="px-3 py-2 text-right font-medium">Imports</th><th className="px-5 py-2 text-right font-medium">Coût Claude (USD)</th></tr></thead>
            <tbody className="divide-y divide-dd-light">
              {stats.par_mois.map((m) => (
                <tr key={m.mois}><td className="px-5 py-2 font-medium text-dd-night">{m.mois}</td><td className="px-3 py-2 text-right tabular-nums">{m.inscriptions}</td><td className="px-3 py-2 text-right tabular-nums">{m.constats}</td><td className="px-3 py-2 text-right tabular-nums">{m.imports}</td><td className="px-5 py-2 text-right tabular-nums">{m.cout_usd.toFixed(2)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-3 text-dd-warm" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une gérance, un e-mail…" className="pl-10 h-11 bg-white" data-testid="master-search" />
        </div>
        <Select value={filtre} onValueChange={setFiltre}>
          <SelectTrigger className="h-11 w-full sm:w-48 bg-white" data-testid="master-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les statuts</SelectItem>
            {Object.entries(STATUT).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => load()} className="h-11 bg-white" data-testid="master-refresh"><RefreshCw size={16} className="mr-2" /> Actualiser</Button>
        <Button variant="outline" onClick={exportCsv} className="h-11 bg-white" data-testid="master-export-csv"><Download size={16} className="mr-2" /> Export CSV</Button>
      </div>

      <div className="bg-white rounded-lg border border-dd-light overflow-x-auto">
        <table className="w-full text-sm" data-testid="master-agences-table">
          <thead><tr className="text-left text-xs text-dd-warm border-b border-dd-light">
            <th className="px-5 py-3 font-medium">Gérance</th><th className="px-3 py-3 font-medium">Inscrite le</th><th className="px-3 py-3 font-medium">Administrateur</th>
            <th className="px-3 py-3 font-medium">Statut</th><th className="px-3 py-3 font-medium text-right">Utilisateurs</th><th className="px-3 py-3 font-medium text-right">Constats</th><th className="px-5 py-3 font-medium">Dernier accès</th></tr></thead>
          <tbody className="divide-y divide-dd-light">
            {rows.map((a) => (
              <tr key={a.id} onClick={() => setSel(a.id)} className="hover:bg-dd-light/40 cursor-pointer" data-testid={`master-row-${a.slug}`}>
                <td className="px-5 py-3 font-medium text-dd-blue">{a.nom}{a.par_defaut && <span className="ml-2 text-[10px] text-dd-warm">(par défaut)</span>}</td>
                <td className="px-3 py-3">{fmtDate(a.created_at)}</td>
                <td className="px-3 py-3">
                  <div className="text-dd-night">{a.admin_nom || "—"}</div>
                  <div className="text-xs text-dd-warm">{a.admin_email} {a.admin_email && !a.email_verifie && <span className="text-amber-700 font-medium">· non confirmé</span>}</div>
                </td>
                <td className="px-3 py-3">
                  <StatutBadge a={a} />
                  {a.statut === "essai" && a.fin_essai && <div className="text-[11px] text-dd-warm mt-1">fin {fmtDate(a.fin_essai)}</div>}
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{a.nb_utilisateurs}</td>
                <td className="px-3 py-3 text-right tabular-nums">{a.nb_constats}</td>
                <td className="px-5 py-3 text-dd-warm">{fmtDateTime(a.dernier_acces)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-5 py-8 text-center text-dd-warm">Aucune gérance</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-lg border border-dd-light p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-dd-warm uppercase tracking-wide"><Icon size={14} /> {label}</div>
      <div className="text-3xl font-bold text-dd-blue mt-1 tabular-nums">{value ?? 0}</div>
      {sub && <div className="text-xs text-dd-warm mt-1">{sub}</div>}
    </div>
  );
}

function AgenceDetail({ id, onBack }) {
  const [a, setA] = useState(null);
  const [notes, setNotes] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const load = () => api.get(`/master/agences/${id}`).then((r) => { setA(r.data); setNotes(r.data.notes || ""); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [id]);

  const patch = async (body, msg) => {
    try { await api.patch(`/master/agences/${id}`, body); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const renvoyer = async () => {
    try { const { data } = await api.post(`/master/agences/${id}/renvoyer-confirmation`); toast.success(data.message); } catch (e) { toast.error(apiError(e)); }
  };
  const definirDefaut = async () => {
    if (!window.confirm(`Faire de « ${a.nom} » la gérance par défaut ? Son identité visuelle sera utilisée sur les pages publiques (connexion, liens).`)) return;
    try { const { data } = await api.post(`/master/agences/${id}/par-defaut`); setA(data); toast.success("Gérance par défaut mise à jour"); } catch (e) { toast.error(apiError(e)); }
  };
  const support = async () => {
    try {
      await api.post(`/master/agences/${id}/support-session`);
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success(`Mode support : ${a.nom} (lecture seule)`);
      navigate("/tableau-de-bord");
    } catch (e) { toast.error(apiError(e)); }
  };
  const supprimer = async () => {
    const s = window.prompt(`Suppression définitive de « ${a.nom} » et de toutes ses données (${a.nb_constats} constats, ${a.nb_utilisateurs} utilisateurs).\nTapez le slug « ${a.slug} » pour confirmer :`);
    if (s !== a.slug) return;
    try { const { data } = await api.delete(`/master/agences/${id}`, { params: { confirm: s } }); toast.success(data.message); onBack(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!a) return <div className="text-dd-warm">Chargement…</div>;
  return (
    <div data-testid="master-agence-detail">
      <button onClick={onBack} className="flex items-center text-sm text-dd-blue hover:underline mb-4" data-testid="master-detail-back"><ArrowLeft size={16} className="mr-1" /> Toutes les gérances</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dd-blue">{a.nom}</h1>
          <p className="text-sm text-dd-warm">Inscrite le {fmtDate(a.created_at)} · slug <code>{a.slug}</code> · dernier accès {fmtDateTime(a.dernier_acces)}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={a.statut} onValueChange={(v) => patch({ statut: v }, `Statut : ${STATUT[v][0]}`)}>
            <SelectTrigger className="h-11 w-44 bg-white" data-testid="master-statut-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUT).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {a.statut === "essai" && (
            <label className="flex items-center gap-2 text-sm text-dd-night bg-white border border-dd-light rounded-md px-3 h-11">
              Fin d'essai
              <input type="date" value={(a.fin_essai || "").slice(0, 10)} onChange={(e) => patch({ fin_essai: e.target.value }, "Date de fin d'essai mise à jour")}
                className="bg-transparent outline-none text-dd-blue font-medium" data-testid="master-fin-essai" />
            </label>
          )}
          {!a.email_verifie && <Button variant="outline" onClick={renvoyer} className="h-11 bg-white" data-testid="master-resend-btn"><Mail size={16} className="mr-2" /> Renvoyer la confirmation</Button>}
          {!a.par_defaut && <Button variant="outline" onClick={supprimer} className="h-11 border-destructive text-destructive hover:bg-destructive/10" data-testid="master-delete-btn"><Trash2 size={16} className="mr-2" /> Supprimer</Button>}
          {!a.par_defaut && <Button variant="outline" onClick={definirDefaut} className="h-11 bg-white" data-testid="master-default-btn"><Star size={16} className="mr-2" /> Définir par défaut</Button>}
          <Button onClick={support} className="h-11 bg-dd-blue hover:bg-dd-night text-white" data-testid="master-support-btn"><LifeBuoy size={16} className="mr-2" /> Ouvrir en lecture seule (support)</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Users} label="Utilisateurs" value={a.nb_utilisateurs} />
        <Kpi icon={FileText} label="Constats" value={a.nb_constats} />
        <Kpi icon={Sparkles} label="Imports Claude" value={a.nb_imports} sub={`≈ ${a.cout_imports_usd.toFixed(2)} USD`} />
        <Kpi icon={Building2} label="Statut" value={(PHASE[a.acces?.phase] || STATUT[a.statut])[0]} sub={a.statut === "essai" && a.acces?.fin_essai ? `Essai jusqu'au ${fmtDate(a.acces.fin_essai)} · lecture jusqu'au ${fmtDate(a.acces.fin_lecture)}` : undefined} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <MasterUtilisateurs aid={id} users={a.utilisateurs} reload={load} onSupport={support} />
        <MasterAbonnement aid={id} abo={a.abonnement} reload={load} />
        <MasterUsageIA aid={id} />
        <MasterJournaux aid={id} />
        <section className="bg-white rounded-lg border border-dd-light" data-testid="master-historique">
          <div className="px-5 py-3 border-b border-dd-light font-semibold text-dd-blue">Historique des statuts</div>
          <ul className="divide-y divide-dd-light text-sm">
            {a.historique_statuts.map((h, i) => (
              <li key={`${h.at}-${i}`} className="px-5 py-2.5 flex flex-wrap items-center gap-2" data-testid={`master-historique-${i}`}>
                <span className="text-xs text-dd-warm tabular-nums w-32">{fmtDateTime(h.at)}</span>
                <Badge className={`border-0 ${(STATUT[h.de] || ["", ""])[1]}`}>{(STATUT[h.de] || [h.de])[0]}</Badge>
                <span className="text-dd-warm">→</span>
                <Badge className={`border-0 ${(STATUT[h.vers] || ["", ""])[1]}`}>{(STATUT[h.vers] || [h.vers])[0]}</Badge>
                <span className="text-xs text-dd-warm">par {h.par}</span>
              </li>
            ))}
            {!a.historique_statuts.length && <li className="px-5 py-4 text-dd-warm">Aucun changement de statut enregistré</li>}
          </ul>
        </section>
        <section className="bg-white rounded-lg border border-dd-light" data-testid="master-support-journal">
          <div className="px-5 py-3 border-b border-dd-light font-semibold text-dd-blue">Journal des sessions support</div>
          <ul className="divide-y divide-dd-light text-sm">
            {a.support_sessions.map((s, i) => (
              <li key={`${s.at || s.debut || ""}-${i}`} className="px-5 py-2.5 flex items-center gap-3" data-testid={`master-support-${i}`}>
                <LifeBuoy size={14} className="text-sky-700 shrink-0" />
                <span className="text-xs text-dd-warm tabular-nums w-32">{fmtDateTime(s.at)}</span>
                <span className="text-dd-night">Lecture seule ouverte par <strong>{s.par}</strong></span>
              </li>
            ))}
            {!a.support_sessions.length && <li className="px-5 py-4 text-dd-warm">Aucune session support</li>}
          </ul>
        </section>
        <section className="bg-white rounded-lg border border-dd-light">
          <div className="px-5 py-3 border-b border-dd-light font-semibold text-dd-blue">Notes internes</div>
          <div className="p-4 space-y-3">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} placeholder="Contact, offre envoyée, échéance d'essai, remarques…" data-testid="master-notes" />
            <Button onClick={() => patch({ notes }, "Notes enregistrées")} className="h-10 bg-dd-blue hover:bg-dd-night text-white" data-testid="master-notes-save">Enregistrer les notes</Button>
          </div>
        </section>
        <section className="bg-white rounded-lg border border-dd-light xl:col-span-2">
          <div className="px-5 py-3 border-b border-dd-light font-semibold text-dd-blue">Derniers constats</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-dd-light">
              {a.derniers_constats.map((c) => (
                <tr key={c.id}><td className="px-5 py-2 font-medium text-dd-night">{c.numero_dossier || "—"}</td><td className="px-3 py-2">{c.immeuble}</td><td className="px-3 py-2 text-xs">{c.type_constat}</td><td className="px-3 py-2 text-xs">{c.statut}</td><td className="px-5 py-2 text-xs text-dd-warm text-right">{fmtDate(c.date_constat)}</td></tr>
              ))}
              {!a.derniers_constats.length && <tr><td className="px-5 py-4 text-dd-warm">Aucun constat</td></tr>}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
